import { Dispatch, SetStateAction, useMemo } from "react";
import {
  ALL_AREA_SYMBOLS,
  ALL_ZONE_SYMBOLS,
  PLAN_AREAS,
  PLAN_ZONES,
  PlanAreaSymbol,
  PlanZoneSymbol,
} from "@/src/lib/planStyles";
import { PlanMeta } from "@/src/api/Plan";

/**
 * Panel legendy dla mapy Planu Ogólnego.
 *
 * Dwa bloki:
 *  1. 13 stref planistycznych (SW..SK) — kolorowy kwadracik + nazwa + checkbox
 *  2. 3 obszary specjalne (OUZ, OZS, OSD) — przerywana linia + nazwa + checkbox
 *
 * Licznik obok każdego wiersza: liczba feature'ów dla danego symbolu
 * (z `meta.symbolCounts`). Symbole nieobecne w planie są wyszarzone
 * i wyłączone (disabled checkbox).
 *
 * Stan widoczności trzymany w parent komponencie jako `Set<string>`.
 * Pusty zbiór = pokazuj wszystkie (konwencja "nic nie zaznaczone = brak filtra");
 * niepusty zbiór = pokazuj tylko te symbole.
 */

export type PlanLegendProps = {
  meta: PlanMeta | null | undefined;
  visibleSymbols: Set<string>;
  setVisibleSymbols: Dispatch<SetStateAction<Set<string>>>;
};

function PlanLegend({ meta, visibleSymbols, setVisibleSymbols }: PlanLegendProps) {
  // Mapping symbol → count (z meta.symbolCounts). Brakujący symbol = 0.
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    if (meta?.symbolCounts) {
      for (const c of meta.symbolCounts) m.set(c.symbol, c.count);
    }
    return m;
  }, [meta]);

  const toggle = (symbol: string) => {
    setVisibleSymbols((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set<string>([...ALL_ZONE_SYMBOLS, ...ALL_AREA_SYMBOLS]);
    setVisibleSymbols(all);
  };
  const selectNone = () => setVisibleSymbols(new Set());

  const isChecked = (s: string) => visibleSymbols.size === 0 || visibleSymbols.has(s);
  const isAbsent = (s: string) => (counts.get(s) ?? 0) === 0;

  return (
    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <strong>Legenda</strong>
        <span>
          <button type="button" onClick={selectAll} style={linkBtn}>
            wszystkie
          </button>{" "}
          ·{" "}
          <button type="button" onClick={selectNone} style={linkBtn}>
            żadne
          </button>
        </span>
      </div>

      <div style={{ marginBottom: 10, color: "#6b7280", fontSize: 12 }}>
        {visibleSymbols.size === 0
          ? "Widoczne: wszystkie obecne w planie"
          : `Widoczne: ${visibleSymbols.size} symboli`}
      </div>

      <details open>
        <summary style={summaryStyle}>Strefy planistyczne (13)</summary>
        {ALL_ZONE_SYMBOLS.map((s) => renderRow(s, PLAN_ZONES[s as PlanZoneSymbol], counts, isChecked, isAbsent, toggle))}
      </details>

      <details open style={{ marginTop: 10 }}>
        <summary style={summaryStyle}>Obszary specjalne (3)</summary>
        {ALL_AREA_SYMBOLS.map((s) =>
          renderAreaRow(s, PLAN_AREAS[s as PlanAreaSymbol], counts, isChecked, isAbsent, toggle),
        )}
      </details>
    </div>
  );
}

function renderRow(
  sym: string,
  z: typeof PLAN_ZONES[PlanZoneSymbol],
  counts: Map<string, number>,
  isChecked: (s: string) => boolean,
  isAbsent: (s: string) => boolean,
  toggle: (s: string) => void,
) {
  const count = counts.get(sym) ?? 0;
  const absent = isAbsent(sym);
  return (
    <label
      key={sym}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "3px 0",
        opacity: absent ? 0.45 : 1,
        cursor: absent ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={isChecked(sym)}
        disabled={absent}
        onChange={() => toggle(sym)}
      />
      <span
        style={{
          display: "inline-block",
          width: 20,
          height: 14,
          background: z.fill,
          border: `1px solid ${z.stroke}`,
          flexShrink: 0,
        }}
      />
      <span style={{ fontFamily: "monospace", minWidth: 32 }}>{sym}</span>
      <span style={{ flex: 1 }}>{z.name}</span>
      <span style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>{count}</span>
    </label>
  );
}

function renderAreaRow(
  sym: string,
  a: typeof PLAN_AREAS[PlanAreaSymbol],
  counts: Map<string, number>,
  isChecked: (s: string) => boolean,
  isAbsent: (s: string) => boolean,
  toggle: (s: string) => void,
) {
  const count = counts.get(sym) ?? 0;
  const absent = isAbsent(sym);
  return (
    <label
      key={sym}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "3px 0",
        opacity: absent ? 0.45 : 1,
        cursor: absent ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={isChecked(sym)}
        disabled={absent}
        onChange={() => toggle(sym)}
      />
      <span
        style={{
          display: "inline-block",
          width: 20,
          height: 14,
          background: "rgba(3,83,83,0.08)",
          borderTop: `2px dashed ${a.stroke}`,
          borderBottom: `2px dashed ${a.stroke}`,
          borderLeft: `1px solid ${a.stroke}`,
          borderRight: `1px solid ${a.stroke}`,
          flexShrink: 0,
        }}
      />
      <span style={{ fontFamily: "monospace", minWidth: 32 }}>{sym}</span>
      <span style={{ flex: 1 }}>{a.name}</span>
      <span style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>{count}</span>
    </label>
  );
}

const summaryStyle: React.CSSProperties = {
  cursor: "pointer",
  fontWeight: 600,
  padding: "4px 0",
  borderBottom: "1px solid #e5e7eb",
  marginBottom: 4,
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#2563eb",
  cursor: "pointer",
  textDecoration: "underline",
  fontSize: 12,
};

export default PlanLegend;
